import React, { Component } from "react";
import PropTypes from "prop-types";

import LineSeries from "./LineSeries";
import StraightLine from "./StraightLine";

const normalizeStroke = (stroke, fallback) => ({
  color: stroke?.color || fallback.color,
  dash: stroke?.dash || fallback.dash,
  width: stroke?.width ?? fallback.width,
  opacity: stroke?.opacity ?? fallback.opacity,
});

// Paleta default do engine Silverlight (observada no OOB vivo).
const FALLBACK_RSI = { color: "#FFFF00", dash: "Solid", width: 1, opacity: 1 };
const FALLBACK_EMA = { color: "#FF0000", dash: "Solid", width: 2, opacity: 1 };

// RSICutler: 2 linhas de dados (RSI + sinal EMARSI) + 3 linhas de referência
// (centro 50, sobrecompra 70, sobrevenda 30). Modelado na VortexSeries.
class RSICutlerSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorRsi = this.yAccessorRsi.bind(this);
    this.yAccessorEma = this.yAccessorEma.bind(this);
  }

  static yExtents(datum, index, allData, indicator) {
    const dataArr = Array.isArray(allData)
      ? allData
      : Array.isArray(datum)
        ? datum
        : [datum];

    if (!indicator || !indicator.renderKey) return [0, 100];

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    dataArr.forEach((item) => {
      const node = item && item[indicator.renderKey];
      if (!node || typeof node !== "object") return;
      [node.rsi, node.emaRsi].forEach((v) => {
        if (!Number.isFinite(v)) return;
        if (v < min) min = v;
        if (v > max) max = v;
      });
    });
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 100];

    // Inclui as linhas de referência visíveis na faixa
    const appearance = indicator?.appearance || {};
    const upper = appearance.overBought?.value ?? 70;
    const lower = appearance.overSold?.value ?? 30;
    min = Math.min(min, lower);
    max = Math.max(max, upper);

    const padding = (max - min) * 0.05 || 1;
    // RSI ∈ [0,100] — clamp nos limites matemáticos (auto-fit como o engine)
    return [Math.max(0, min - padding), Math.min(100, max + padding)];
  }

  yAccessorRsi(d) {
    const node = this.props.yAccessor(d);
    return node && node.rsi;
  }

  yAccessorEma(d) {
    const node = this.props.yAccessor(d);
    return node && node.emaRsi;
  }

  render() {
    const {
      className,
      strokeRsi,
      strokeEmaRsi,
      centerLine,
      overBought,
      overSold,
    } = this.props;

    const rsiStroke = normalizeStroke(strokeRsi, FALLBACK_RSI);
    const emaStroke = normalizeStroke(strokeEmaRsi, FALLBACK_EMA);

    const extract = (prop, defaultValue, defaultStroke) => {
      if (typeof prop === "number")
        return { value: prop, stroke: defaultStroke };
      if (prop && typeof prop === "object" && prop.value !== undefined) {
        return { value: prop.value, stroke: prop.stroke || defaultStroke };
      }
      return { value: defaultValue, stroke: defaultStroke };
    };

    const center = extract(centerLine, 50, {
      color: "#0000B4",
      dash: "Dot",
      width: 1,
      opacity: 1,
    });
    const upper = extract(overBought, 70, {
      color: "#A9A9A9",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });
    const lower = extract(overSold, 30, {
      color: "#1E90FF",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });

    return (
      <g className={className}>
        <StraightLine stroke={center.stroke} yValue={center.value} />
        <StraightLine stroke={upper.stroke} yValue={upper.value} />
        <StraightLine stroke={lower.stroke} yValue={lower.value} />
        <LineSeries
          yAccessor={this.yAccessorRsi}
          stroke={rsiStroke}
          connectNulls
          highlightOnHover={false}
        />
        <LineSeries
          yAccessor={this.yAccessorEma}
          stroke={emaStroke}
          connectNulls
          highlightOnHover={false}
        />
      </g>
    );
  }
}

RSICutlerSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  strokeRsi: PropTypes.object,
  strokeEmaRsi: PropTypes.object,
  centerLine: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  overBought: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  overSold: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
};

RSICutlerSeries.defaultProps = {
  className: "react-stockcharts-rsicutler-series",
  strokeRsi: FALLBACK_RSI,
  strokeEmaRsi: FALLBACK_EMA,
  centerLine: 50,
  overBought: 70,
  overSold: 30,
};

export default RSICutlerSeries;
